import { useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import Layout from '../../../common/Layout';
import CourseData from '../../../data/course/CourseData.json';
import CourseProgramLayout from '../courseDetails/CourseProgramLayout';
import { IoClose } from "react-icons/io5";
import FooterTwo from '../../../common/footer/FooterTwo';
import HeaderTwo from '../../../common/header/HeaderTwo';

export const programs = [
    {
        id: 1,
        programGroup: 'general-english',
        title: 'General English',
        image: 'assets/images/course/course-01/course-01.jpg',
        caption: 'Build Your English Foundation',
        body:
            'Improve your everyday English skills through interactive lessons focused on speaking, listening, reading, and writing.',
    },
    {
        id: 2,
        programGroup: 'ielts',
        title: 'IELTS',
        image: 'assets/images/course/course-01/course-05.jpg',
        caption: 'Achieve Your Target Score',
        body:
            'Prepare for the IELTS test with structured strategies, practice tests, and expert guidance to help you reach your desired band score.',
    },
    {
        id: 3,
        programGroup: 'j1',
        title: 'English Preparation for J1',
        image: 'assets/images/course/course-01/course-19.jpg',
        caption: 'Get Ready for Your J-1 Journey',
        body:
            'Develop workplace communication skills, interview confidence, and cultural understanding needed for a successful J-1 program.',
    },
    {
        id: 4,
        programGroup: 'toefl',
        title: 'TOEFL',
        image: 'assets/images/course/course-01/course-09.jpg',
        caption: 'Academic English Excellence',
        body:
            'Enhance your academic English skills and master the TOEFL exam with focused training and exam-oriented strategies.',
    },
    {
        id: 5,
        programGroup: 'duolingo',
        title: 'Duolingo',
        image: 'assets/images/course/course-01/course-13.jpg',
        caption: 'Fast & Modern Test Prep',
        body:
            'Get ready for the Duolingo English Test with efficient practice, adaptive test strategies, and real exam-style questions.',
    },
    {
        id: 6,
        programGroup: 'ipass',
        title: 'iPass',
        image: 'assets/images/course/course-01/course-15.jpg',
        caption: 'Your Pathway to New Zealand',
        body:
            'A comprehensive English pathway program designed to help you succeed in study or work in New Zealand.',
    },
];

const Courses = () => {
    const [modalOpen, setModalOpen] = useState(false);
    const [activeProgram, setActiveProgram] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [selectedCourse, setSelectedCourse] = useState(null);
    const [selectedMeetKey, setSelectedMeetKey] = useState(null);
    const [selectedItemId, setSelectedItemId] = useState(null);

    const groupCourses = useMemo(() => {
        if (!activeProgram?.programGroup) return [];
        return CourseData.filter((c) => c.programGroup === activeProgram.programGroup);
    }, [activeProgram]);

    const getMeetKeysToShow = (courses) => {
        const meetKeyLists = courses.map((c) => Object.keys(c?.meetOptions || {}));
        if (meetKeyLists.length === 0) return [];

        const common = meetKeyLists[0].filter((key) => meetKeyLists.every((list) => list.includes(key)));
        if (common.length > 0) return common;

        return [...new Set(meetKeyLists.flat())];
    };

    const meetItems = useMemo(() => {
        if (!activeProgram || !groupCourses.length) return [];

        const meetKeysToShow = getMeetKeysToShow(groupCourses);
        const items = [];

        groupCourses.forEach(courseRow => {
            meetKeysToShow.forEach(meetKey => {
                const meet = courseRow?.meetOptions?.[meetKey];
                if (!meet) return;
                const classMeetId = Number(meetKey.replace('x', ''));

                items.push({
                    id_ui: `${courseRow.id}-${meetKey}`,
                    id_detail: courseRow.id,
                    id_program: courseRow.id_program,
                    programName: courseRow.program?.nama_program ?? courseRow.title,
                    id_type: meet.id_type,
                    classMeetLabel: meet.label,
                    image: courseRow.image,
                    title: `${courseRow.title} - ${meet.label}`,
                    course: courseRow,
                    meetKey,
                });
            });
        });

        return items;
    }, [activeProgram, groupCourses]);

    const selectProgramItem = (item) => {
        if (!item) return;
        setSelectedItem(item);
        setSelectedCourse(item.course);
        setSelectedMeetKey(item.meetKey);
        setSelectedItemId(item.id_ui);
    }

    const openModal = (program) => {
        const list = CourseData.filter((c) => c.programGroup === program.programGroup);
        const meetKeysToShow = getMeetKeysToShow(list);

        const firstCourse = list[0] || null;
        const firstMeetKey = firstCourse?.meetOptions?.[meetKeysToShow[0]] ? meetKeysToShow[0] : Object.keys(firstCourse?.meetOptions || {})[0] || null;

        setActiveProgram(program);
        setSelectedCourse(firstCourse);
        setSelectedMeetKey(firstMeetKey);
        setSelectedItemId(firstCourse && firstMeetKey ? `${firstCourse.id}-${firstMeetKey}` : null);
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setActiveProgram(null);
        setSelectedCourse(null);
        setSelectedMeetKey(null);
        setSelectedItemId(null);
    };

    return (
        <div>
            <HeaderTwo />
            <div className='section-padding-top'>
                <div className="container">
                    <div className="program-grid">
                        {programs.map((program) => (
                            <div key={program.id} className="program-card hover-lift">
                                <img
                                    src={program.image}
                                    alt={program.title}
                                    className="program-image"
                                />

                                <div className="program-content">
                                    <span className="program-caption">{program.caption}</span>
                                    <h3 className="program-title">{program.title}</h3>
                                    <p className="program-body">{program.body}</p>

                                    <button
                                        type="button"
                                        className="program-cta"
                                        onClick={() => openModal(program)}
                                    >
                                        View More
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <Modal show={modalOpen} onHide={closeModal} size="xl" centered scrollable>
                    <Modal.Header>
                        <Modal.Title>{activeProgram?.title}</Modal.Title>
                        <button type="button" className="btn-close-custom" onClick={closeModal}><IoClose size={32} /></button>
                    </Modal.Header>
                    <Modal.Body className="">
                        {activeProgram && selectedCourse && meetItems.length > 0 && (
                            <CourseProgramLayout
                                headingTitle={activeProgram.title}
                                showPageHeading={false}
                                items={meetItems}
                                selectedCourse={selectedCourse}
                                selectedMeetKey={selectedMeetKey}
                                selectedItemId={selectedItemId}
                                meetItems={meetItems}
                                selectedItem={selectedItem}
                                onSelectItem={selectProgramItem}
                            />
                        )}
                    </Modal.Body>
                </Modal>
            </div>
            <FooterTwo />
        </div>
    );
};

export default Courses;